import { observer } from "mobx-react-lite";
import { FormikProvider, useFormik } from "formik";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDebounce } from "use-debounce";

import { PATHS } from "@/lib/router";
import { useTran, useUnsavedChanges } from "@/hooks";
import { UnsavedChangesDialog } from "@/components/common";
import { useCompanyStore } from "../hooks";
import {
  buildCompanyStepSchema,
  getCompanyStepFieldKeys,
  validateCompanyFormStep,
  validateCompanySubmitForm,
} from "../schemas";
import CompanySetupHeader from "./CompanySetupHeader";
import CompanySetupStepper from "./CompanySetupStepper";
import CompanySetupStepContent from "./CompanySetupStepContent";
import CompanySetupFooter from "./CompanySetupFooter";

const COMPANY_FORM_DEFAULTS = {
  companyName: "",
  country: "",
  industry: "",
  employeesRange: "",
  revenueRange: "",
  serviceCountries: [],
  objectives: [],
  targetFrequency: "yearly",
  enableMonthlyYearlySwitch: false,
  enableViewDetailsSwitch: false,
  showCategoryDashboardWidgets: true,
  showOnlyMetricsWithTargetAndData: false,
};

const SAMPLE_FORM_VALUES = {
  companyName: "Acme Inc.",
  country: "singapore",
  industry: "food_083",
  employeesRange: "51 - 200",
  revenueRange: "2M - 10M",
  serviceCountries: ["singapore", "taiwan"],
  objectives: ["electricity", "fuel"],
  targetFrequency: "yearly",
  enableMonthlyYearlySwitch: false,
  enableViewDetailsSwitch: false,
  showCategoryDashboardWidgets: true,
  showOnlyMetricsWithTargetAndData: false,
};

const getCompanyId = (data) => data?.id ?? data?._id ?? data?.companyId ?? null;

const buildCompanyFormPatch = (company) => {
  if (!company || typeof company !== "object") return null;
  const patch = {};
  const name = company.companyName ?? company.name;
  if (name) patch.companyName = name;
  if (company.country) patch.country = company.country;
  if (company.industry) patch.industry = company.industry;
  if (company.employeesRange) patch.employeesRange = company.employeesRange;
  if (company.revenueRange) patch.revenueRange = company.revenueRange;
  if (Array.isArray(company.serviceCountries)) {
    patch.serviceCountries = company.serviceCountries;
  }
  if (Array.isArray(company.objectives)) patch.objectives = company.objectives;
  if (company.targetFrequency) patch.targetFrequency = company.targetFrequency;
  if (typeof company.enableMonthlyYearlySwitch === "boolean") {
    patch.enableMonthlyYearlySwitch = company.enableMonthlyYearlySwitch;
  }
  if (typeof company.enableViewDetailsSwitch === "boolean") {
    patch.enableViewDetailsSwitch = company.enableViewDetailsSwitch;
  }
  if (typeof company.showCategoryDashboardWidgets === "boolean") {
    patch.showCategoryDashboardWidgets = company.showCategoryDashboardWidgets;
  }
  if (typeof company.showOnlyMetricsWithTargetAndData === "boolean") {
    patch.showOnlyMetricsWithTargetAndData =
      company.showOnlyMetricsWithTargetAndData;
  }
  return Object.keys(patch).length > 0 ? patch : null;
};

const normalizeCompanyFormValues = (form = {}) => ({
  companyName: form?.companyName || "",
  country: form?.country || "",
  industry: form?.industry || "",
  employeesRange: form?.employeesRange || "",
  revenueRange: form?.revenueRange || "",
  serviceCountries: Array.isArray(form?.serviceCountries)
    ? form.serviceCountries
    : [],
  objectives: Array.isArray(form?.objectives) ? form.objectives : [],
  targetFrequency: form?.targetFrequency === "monthly" ? "monthly" : "yearly",
  enableMonthlyYearlySwitch: form?.enableMonthlyYearlySwitch ?? false,
  enableViewDetailsSwitch: form?.enableViewDetailsSwitch ?? false,
  showCategoryDashboardWidgets: form?.showCategoryDashboardWidgets ?? true,
  showOnlyMetricsWithTargetAndData:
    form?.showOnlyMetricsWithTargetAndData ?? false,
});

const buildTouchedPatchForStep = (step = 1) => {
  const patch = {};
  getCompanyStepFieldKeys(step).forEach((key) => {
    patch[key] = true;
  });
  if (step === 4) {
    patch.step4 = true;
  }
  return patch;
};

const getStepFromValidationErrors = (errors = {}) => {
  if (errors.companyName || errors.country) return 1;
  if (errors.industry) return 2;
  if (errors.employeesRange || errors.revenueRange) return 3;
  if (errors.serviceCountries || errors.objectives || errors.step4) return 4;
  return null;
};

const countFilledFields = (values = {}) => {
  const keys = [
    "companyName",
    "country",
    "industry",
    "employeesRange",
    "revenueRange",
    "serviceCountries",
    "objectives",
  ];

  return keys.reduce((count, key) => {
    const value = values?.[key];
    if (Array.isArray(value)) return count + (value.length > 0 ? 1 : 0);
    if (typeof value === "string") return count + (value.trim() ? 1 : 0);
    return count;
  }, 0);
};

const getProgressFromValues = (values = {}) => {
  const totalFields = 7;
  return Math.round((countFilledFields(values) / totalFields) * 100);
};

const CompanySetupWizard = observer(
  ({
    isDialog = false,
    companyId = null,
    companyData = null,
    onCompleted,
    compact = false,
    showTargetFrequency = false,
  }) => {
    const { t } = useTran();
    const navigate = useNavigate();
    const location = useLocation();
    const isFromRegister = location.state?.from === "register";

    const {
      step,
      form,
      companies,
      setStep,
      createCompany,
      fetchPublicCompanies,
      hydrateForm,
      reset,
      clearDraft,
      setCompany,
      saveDraftSnapshot,
      setForm,
    } = useCompanyStore();

    const hydratedCompanyRef = useRef(null);
    const hasFetchedCompaniesRef = useRef(false);
    const hasInitializedFirstRegisterResetRef = useRef(false);
    const [companyNameStatus, setCompanyNameStatus] = useState("idle");

    const resolvedCompanyId = useMemo(
      () => companyId ?? getCompanyId(companyData),
      [companyId, companyData],
    );

    const initialValues = useMemo(
      () => normalizeCompanyFormValues(form),
      [form],
    );

    const formik = useFormik({
      initialValues,
      enableReinitialize: true,
      validationSchema: buildCompanyStepSchema(step, t),
      validateOnBlur: true,
      validateOnChange: false,
      onSubmit: () => {},
    });

    const { showDialog, confirmDiscard, cancelDiscard, bypass } =
      useUnsavedChanges(formik.dirty);

    const [debouncedCompanyName] = useDebounce(formik.values.companyName, 400);

    useEffect(() => {
      const resolvedCompanyIdFromData = getCompanyId(companyData);

      if (!companyData || !resolvedCompanyIdFromData) {
        hydratedCompanyRef.current = null;
        if (isDialog) {
          reset();
        }
        return;
      }

      if (hydratedCompanyRef.current === resolvedCompanyIdFromData) {
        return;
      }

      hydratedCompanyRef.current = resolvedCompanyIdFromData;
      const patch = buildCompanyFormPatch(companyData);
      if (patch) {
        hydrateForm(patch);
      }
    }, [companyData, hydrateForm, isDialog, reset]);

    useEffect(() => {
      if (isDialog || !isFromRegister || companyData || resolvedCompanyId) {
        hasInitializedFirstRegisterResetRef.current = false;
        return;
      }
      if (hasInitializedFirstRegisterResetRef.current) return;
      hasInitializedFirstRegisterResetRef.current = true;

      clearDraft();
      reset({ persist: false });
      setCompany(null);
      setCompanyNameStatus("idle");
    }, [
      clearDraft,
      companyData,
      isDialog,
      isFromRegister,
      reset,
      resolvedCompanyId,
      setCompany,
    ]);

    useEffect(() => {
      if (step !== 1) return;
      const normalizedCompanyName = debouncedCompanyName?.trim() || "";
      if (!normalizedCompanyName) {
        setCompanyNameStatus("idle");
        return;
      }

      let isMounted = true;

      const checkAvailability = async () => {
        setCompanyNameStatus("checking");

        try {
          let availableCompanies = companies;
          if (!hasFetchedCompaniesRef.current && companies.length === 0) {
            hasFetchedCompaniesRef.current = true;
            try {
              availableCompanies = await fetchPublicCompanies();
            } catch (error) {
              hasFetchedCompaniesRef.current = false;
              throw error;
            }
          }

          if (!isMounted) return;

          const exactName = normalizedCompanyName.toLowerCase();
          const matchedCompany = availableCompanies.find((company) => {
            const name = (company?.companyName || company?.name || "")
              .trim()
              .toLowerCase();
            return name === exactName;
          });

          const matchedCompanyId =
            matchedCompany?._id ??
            matchedCompany?.id ??
            matchedCompany?.companyId ??
            null;
          const isSameCompany =
            matchedCompany &&
            resolvedCompanyId &&
            String(matchedCompanyId) === String(resolvedCompanyId);

          if (matchedCompany && !isSameCompany) {
            setCompanyNameStatus("taken");
            return;
          }

          setCompanyNameStatus("available");
        } catch {
          if (!isMounted) return;
          setCompanyNameStatus("error");
        }
      };

      checkAvailability();

      return () => {
        isMounted = false;
      };
    }, [
      companies,
      debouncedCompanyName,
      fetchPublicCompanies,
      resolvedCompanyId,
      step,
    ]);

    useEffect(() => {
      saveDraftSnapshot({
        step,
        form: normalizeCompanyFormValues(formik.values),
      });
    }, [formik.values, saveDraftSnapshot, step]);

    const companyNameAvailabilityError =
      companyNameStatus === "taken"
        ? t("Company name is already in use.")
        : undefined;

    const companyNameStatusMessage =
      companyNameStatus === "checking"
        ? t("Checking availability...")
        : companyNameStatus === "available"
          ? t("Company name is available.")
          : companyNameStatus === "error"
            ? t("Unable to verify company name right now.")
            : "";

    const companyNameDescription =
      companyNameStatus === "taken" ? undefined : companyNameStatusMessage;

    const companyNameDescriptionClassName =
      companyNameStatus === "available"
        ? "text-emerald-600 text-xs"
        : companyNameStatus === "error"
          ? "text-amber-600 text-xs"
          : "text-xs text-muted-foreground";

    const isCompanyNamePending = companyNameStatus === "checking";
    const isCompanyNameTaken = companyNameStatus === "taken";

    const markStepFieldsTouched = (stepToMark) => {
      formik.setTouched(
        {
          ...formik.touched,
          ...buildTouchedPatchForStep(stepToMark),
        },
        false,
      );
    };

    const validateSingleStep = (stepToValidate) => {
      const errors = validateCompanyFormStep(stepToValidate, formik.values, t);

      if (stepToValidate === 1 && isCompanyNameTaken) {
        errors.companyName = t("Company name is already in use.");
      }

      return errors;
    };

    const getFirstBlockingStep = (targetStep) => {
      for (let currentStep = 1; currentStep < targetStep; currentStep += 1) {
        const stepErrors = validateSingleStep(currentStep);
        if (Object.keys(stepErrors).length > 0) {
          formik.setErrors(stepErrors);
          markStepFieldsTouched(currentStep);
          return currentStep;
        }
      }

      return null;
    };

    const canNavigateToStep = () => true;

    const goToStep = (targetStep) => {
      if (targetStep <= step) {
        setStep(targetStep);
        return;
      }

      const firstBlockingStep = getFirstBlockingStep(targetStep);
      if (firstBlockingStep) {
        setStep(firstBlockingStep);
        return;
      }

      setStep(targetStep);
    };

    const canContinueStep = step === 1 ? !isCompanyNamePending : true;

    const nextStep = () => {
      if (step === 1 && isCompanyNamePending) {
        return;
      }

      const stepErrors = validateSingleStep(step);
      if (Object.keys(stepErrors).length > 0) {
        formik.setErrors(stepErrors);
        markStepFieldsTouched(step);
        return;
      }

      formik.setErrors({});
      setStep(Math.min(4, step + 1));
    };

    const prevStep = () => setStep(Math.max(1, step - 1));

    const submitCurrentForm = async (nextPath) => {
      const submitErrors = validateCompanySubmitForm(formik.values, t);
      if (isCompanyNameTaken) {
        submitErrors.companyName = t("Company name is already in use.");
      }

      if (Object.keys(submitErrors).length > 0) {
        formik.setErrors(submitErrors);
        const invalidStep = getStepFromValidationErrors(submitErrors);
        if (invalidStep) {
          setStep(invalidStep);
          markStepFieldsTouched(invalidStep);
        }
        return;
      }

      try {
        setForm(normalizeCompanyFormValues(formik.values));
        const savedCompany = await createCompany({
          formValues: formik.values,
          companyId: resolvedCompanyId,
        });
        formik.resetForm({ values: formik.values });

        if (isDialog) {
          onCompleted?.(savedCompany);
          return;
        }

        if (nextPath) {
          bypass();
          navigate(nextPath);
        }
      } catch {
        // toast handled in store
      }
    };

    const handleStart = () => submitCurrentForm(PATHS.DASHBOARD);
    const handleSaveAndExit = () => submitCurrentForm(PATHS.DASHBOARD);
    const handleReview = () => setStep(5);

    const handleSampleData = () => {
      formik.setValues(SAMPLE_FORM_VALUES, false);
      formik.setTouched(
        {
          companyName: true,
          country: true,
          industry: true,
          employeesRange: true,
          revenueRange: true,
        },
        false,
      );
      formik.setErrors({});
    };

    const handleReset = () => {
      clearDraft();
      reset({ persist: false });
      formik.resetForm({
        values: normalizeCompanyFormValues(COMPANY_FORM_DEFAULTS),
      });
      setCompanyNameStatus("idle");
    };

    const progress = useMemo(
      () => getProgressFromValues(formik.values),
      [formik.values],
    );

    const isCompletedSetup = Boolean(resolvedCompanyId);
    const submitErrors = useMemo(
      () => validateCompanySubmitForm(formik.values, t),
      [formik.values, t],
    );
    const canSaveAndExit = isCompletedSetup
      ? formik.dirty
      : Object.keys(submitErrors).length === 0 &&
        !isCompanyNamePending &&
        !isCompanyNameTaken;

    return (
      <FormikProvider value={formik}>
        <UnsavedChangesDialog
          open={showDialog}
          onConfirm={confirmDiscard}
          onCancel={cancelDiscard}
        />
        <div className="w-full space-y-4 sm:space-y-6">
          <CompanySetupHeader
            isDialog={isDialog}
            onSampleData={handleSampleData}
            onReset={handleReset}
            progress={progress}
            isCreateMode={!resolvedCompanyId}
          />
          <div className="grid gap-6 rounded-radius border border-border bg-card p-4 xl:grid-cols-[240px_1fr] sm:p-5">
            <CompanySetupStepper
              canNavigateToStep={canNavigateToStep}
              goToStep={goToStep}
            />
            <div className="flex min-w-0 flex-col justify-between">
              <CompanySetupStepContent
                showTargetFrequency={showTargetFrequency || !resolvedCompanyId}
                companyNameError={companyNameAvailabilityError}
                companyNameDescription={companyNameDescription}
                companyNameDescriptionClassName={
                  companyNameDescriptionClassName
                }
              />
              <CompanySetupFooter
                compact={compact}
                isDialog={isDialog}
                canContinueStep={canContinueStep}
                nextStep={nextStep}
                prevStep={prevStep}
                handleSaveAndExit={handleSaveAndExit}
                handleReview={handleReview}
                handleStart={handleStart}
                isCompletedSetup={isCompletedSetup}
                hasChanges={formik.dirty}
                canSaveAndExit={canSaveAndExit}
              />
            </div>
          </div>
        </div>
      </FormikProvider>
    );
  },
);

export default CompanySetupWizard;
